// LoginModal Component
// Allows existing users to log in with username and password

import React, { useState, useEffect, useRef } from 'react';
import { requestPasswordHelp, getSavedLogins } from '../../services/authService';
import { trackEvent } from '../../services/analyticsService';
import PasswordField from '../PasswordField/PasswordField';
import './LoginModal.css';

const LoginModal = ({ isOpen, onLogin, onClose, onSwitchToRegister }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  // Forgot password files a request; Fahad checks it is really them and
  // sends a new password by hand. Nothing about the account changes here.
  const [mode, setMode] = useState('login'); // login | forgot | sent
  const [contact, setContact] = useState('');
  const [proof, setProof] = useState('');
  const usernameInputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && usernameInputRef.current) {
      setTimeout(() => usernameInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUsername('');
      setPassword('');
      setError('');
      setIsLoading(false);
    }
  }, [isOpen]);

  // Accounts this device signed out of, offered back with one tap.
  const savedLogins = getSavedLogins();
  const signBackIn = async (saved) => {
    setIsLoading(true);
    setError('');
    const result = await onLogin(saved.username, atob(saved.password));
    if (!result.success) {
      setError(result.error || 'Login failed');
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (username.trim().length < 3) {
      setError('Please enter a valid username');
      return;
    }

    if (password.length < 6) {
      setError('Please enter your password');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await onLogin(username.trim(), password);
      if (!result.success) {
        setError(result.error || 'Login failed');
        setIsLoading(false);
      }
      // If successful, parent component will close modal
    } catch (err) {
      setError('Login failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && onClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const sendHelpRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const result = await requestPasswordHelp(username.trim(), contact.trim(), proof.trim());
    setIsLoading(false);
    if (result.success) { setMode('sent'); trackEvent('password_help_request'); }
    else setError(result.error || 'Could not send. Try again.');
  };

  if (mode !== 'login') {
    return (
      <div className="login-modal-overlay" onClick={onClose}>
        <div className="login-modal" onClick={(e) => e.stopPropagation()}>
          {mode === 'sent' ? (
            <div className="modal-header">
              <div className="modal-icon">📨</div>
              <h2 className="modal-title">Request sent</h2>
              <p className="modal-subtitle">
                We will check the account is yours and send a new password to {contact.trim()}.
                Your scores stay on your name.
              </p>
            </div>
          ) : (
            <>
              <div className="modal-header">
                <div className="modal-icon">🗝️</div>
                <h2 className="modal-title">Forgot your password?</h2>
                <p className="modal-subtitle">Tell us your name and how to reach you. We send a new password by hand.</p>
              </div>
              <form onSubmit={sendHelpRequest} className="modal-form">
                <div className="input-container">
                  <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                    placeholder="Your username" className="login-input" disabled={isLoading} autoComplete="username" />
                </div>
                <div className="input-container">
                  <input type="text" value={contact} onChange={(e) => setContact(e.target.value)}
                    placeholder="Email, Instagram or Discord" className="login-input" disabled={isLoading} />
                </div>
                <div className="input-container">
                  <textarea value={proof} onChange={(e) => setProof(e.target.value)} rows={3} maxLength={500}
                    placeholder="Help us know it's you (optional): your avatar, games you played, rough scores"
                    className="login-input login-textarea" disabled={isLoading} />
                </div>
                {error && (
                  <div className="error-message"><span className="error-icon">⚠️</span>{error}</div>
                )}
                <button type="submit" className={`submit-button ${isLoading ? 'submitting' : ''}`}
                  disabled={isLoading || username.trim().length < 3 || contact.trim().length < 3}>
                  {isLoading ? 'Sending...' : 'Send request'}
                </button>
              </form>
            </>
          )}
          <button className="forgot-link" onClick={() => { setMode('login'); setError(''); }} disabled={isLoading}>
            ← Back to log in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-modal-overlay" onClick={onClose} onKeyDown={handleKeyDown}>
      <div className="login-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-icon">🔑</div>
          <h2 className="modal-title">Welcome Back, Pirate!</h2>
          <p className="modal-subtitle">Log in to access your scores</p>
        </div>

        {savedLogins.length > 0 && (
          <div className="saved-logins">
            {savedLogins.map((saved) => (
              <button key={saved.username} type="button" className="saved-login" onClick={() => signBackIn(saved)} disabled={isLoading}>
                Continue as <strong>{saved.username}</strong>
              </button>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="input-container">
            <input
              ref={usernameInputRef}
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                if (error) setError('');
              }}
              placeholder="Username"
              className={`login-input ${error ? 'has-error' : ''}`}
              disabled={isLoading}
              autoComplete="username"
            />
          </div>

          <div className="input-container">
            <PasswordField
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (error) setError('');
              }}
              placeholder="Password"
              className={`login-input ${error ? 'has-error' : ''}`}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            className={`submit-button ${isLoading ? 'submitting' : ''}`}
            disabled={isLoading || username.trim().length < 3 || password.length < 6}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Logging in...
              </>
            ) : (
              <>
                <span className="btn-icon">⚓</span>
                Log In
              </>
            )}
          </button>
        </form>

        <button type="button" className="forgot-link" onClick={() => { setMode('forgot'); setError(''); }} disabled={isLoading}>
          Forgot password?
        </button>

        <div className="modal-footer">
          <p className="footer-text">Don't have an account?</p>
          {onSwitchToRegister && (
            <button
              className="switch-button"
              onClick={onSwitchToRegister}
              disabled={isLoading}
            >
              Create New Account
            </button>
          )}
        </div>

        {onClose && (
          <button className="close-button" onClick={onClose} disabled={isLoading}>
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default LoginModal;
